import { useFrame, useLoader } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import {
  AdditiveBlending,
  BackSide,
  BufferGeometry,
  CanvasTexture,
  Group,
  Line as ThreeLine,
  LineBasicMaterial,
  NoColorSpace,
  ShaderMaterial,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  Vector3 as ThreeVector3,
} from "three";
import {
  EARTH_RADIUS,
  EARTH_ROTATION_SECONDS_AT_ONE_X,
} from "../simulation/constants";
import { getEarthAxis } from "../simulation/geometry";
import type { SimulationState } from "../simulation/state";
import { LabelSprite } from "./LabelSprite";
import { quaternionFromLocalUp } from "./sceneMath";

function createOceanRoughnessMap(source: Texture): CanvasTexture | null {
  const image = source.image as CanvasImageSource | undefined;
  if (
    image === undefined ||
    !("width" in image) ||
    typeof image.width !== "number" ||
    typeof image.height !== "number" ||
    image.width === 0
  ) {
    return null;
  }

  const width = Math.min(image.width, 1024);
  const height = Math.min(image.height, 1024);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (context === null) {
    return null;
  }

  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height);
  const data = pixels.data;

  for (let index = 0; index < data.length; index += 4) {
    const red = data[index] ?? 0;
    const green = data[index + 1] ?? 0;
    const blue = data[index + 2] ?? 0;
    const isOcean = blue > red + 16 && blue > green;
    const roughness = isOcean ? 0.26 : 0.94;
    const value = Math.round(roughness * 255);
    data[index] = value;
    data[index + 1] = value;
    data[index + 2] = value;
  }

  context.putImageData(pixels, 0, 0);
  const roughnessMap = new CanvasTexture(canvas);
  roughnessMap.colorSpace = NoColorSpace;
  roughnessMap.needsUpdate = true;
  return roughnessMap;
}

function Atmosphere() {
  const material = useMemo(
    () =>
      new ShaderMaterial({
        blending: AdditiveBlending,
        depthWrite: false,
        side: BackSide,
        transparent: true,
        vertexShader: `
          varying vec3 vWorldPosition;
          varying vec3 vWorldNormal;
          void main() {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            vWorldNormal = normalize(mat3(modelMatrix) * normal);
            gl_Position = projectionMatrix * viewMatrix * worldPosition;
          }
        `,
        fragmentShader: `
          varying vec3 vWorldPosition;
          varying vec3 vWorldNormal;
          void main() {
            vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
            float fresnel = pow(1.0 - abs(dot(viewDirection, normalize(vWorldNormal))), 2.6);
            gl_FragColor = vec4(0.32, 0.58, 1.0, fresnel * 0.9);
          }
        `,
      }),
    [],
  );

  useEffect(() => () => material.dispose(), [material]);

  return (
    <mesh scale={1.075}>
      <sphereGeometry args={[EARTH_RADIUS, 48, 24]} />
      <primitive attach="material" object={material} />
    </mesh>
  );
}

function circlePoints(radius: number, steps: number) {
  return Array.from({ length: steps + 1 }, (_, index) => {
    const angle = (index / steps) * Math.PI * 2;
    return new ThreeVector3(
      Math.cos(angle) * radius,
      0,
      Math.sin(angle) * radius,
    );
  });
}

export function Earth({
  isPlaying,
  speed,
  showRotation,
  classroomMode,
}: {
  readonly isPlaying: boolean;
  readonly speed: SimulationState["speed"];
  readonly showRotation: boolean;
  readonly classroomMode: boolean;
}) {
  const sourceTexture = useLoader(
    TextureLoader,
    "/textures/earth-blue-marble.jpg",
  );
  const texture = useMemo(() => {
    const nextTexture = sourceTexture.clone();
    nextTexture.anisotropy = 8;
    nextTexture.colorSpace = SRGBColorSpace;
    nextTexture.needsUpdate = true;
    return nextTexture;
  }, [sourceTexture]);
  const roughnessMap = useMemo(
    () => createOceanRoughnessMap(sourceTexture),
    [sourceTexture],
  );

  const sphereGroup = useRef<Group>(null);
  const earthAxis = useMemo(() => getEarthAxis(), []);
  const earthQuaternion = useMemo(
    () => quaternionFromLocalUp(earthAxis),
    [earthAxis],
  );
  const equatorGeometry = useMemo(
    () =>
      new BufferGeometry().setFromPoints(circlePoints(EARTH_RADIUS * 1.03, 96)),
    [],
  );
  const axisGeometry = useMemo(() => {
    const axis = new ThreeVector3(...earthAxis).multiplyScalar(
      EARTH_RADIUS * 1.55,
    );
    return new BufferGeometry().setFromPoints([
      axis.clone().multiplyScalar(-1),
      axis,
    ]);
  }, [earthAxis]);
  const axisLine = useMemo(
    () =>
      new ThreeLine(
        axisGeometry,
        new LineBasicMaterial({
          color: "#f5f8ff",
          opacity: 0.9,
          transparent: true,
        }),
      ),
    [axisGeometry],
  );

  useEffect(
    () => () => {
      equatorGeometry.dispose();
      axisGeometry.dispose();
      axisLine.material.dispose();
      roughnessMap?.dispose();
      texture.dispose();
    },
    [axisGeometry, axisLine.material, equatorGeometry, roughnessMap, texture],
  );

  useFrame((_, delta) => {
    if (sphereGroup.current !== null && isPlaying && showRotation) {
      sphereGroup.current.rotation.y +=
        (delta * speed * Math.PI * 2) / EARTH_ROTATION_SECONDS_AT_ONE_X;
    }
  });

  return (
    <group>
      <group quaternion={earthQuaternion}>
        <group ref={sphereGroup}>
          <mesh>
            <sphereGeometry args={[EARTH_RADIUS, 64, 32]} />
            <meshStandardMaterial
              emissive="#6f93bc"
              emissiveIntensity={0.14}
              emissiveMap={texture}
              map={texture}
              metalness={0.14}
              roughness={1}
              roughnessMap={roughnessMap}
            />
          </mesh>
        </group>
        <Atmosphere />
        <lineLoop geometry={equatorGeometry}>
          <lineBasicMaterial color="#7ed6df" transparent opacity={0.88} />
        </lineLoop>
      </group>
      <primitive object={axisLine} />
      <LabelSprite
        text={classroomMode ? "Earth — your head" : "Earth"}
        position={[0, -(EARTH_RADIUS + 0.9), 0]}
        scale={classroomMode ? [3.2, 0.55, 1] : [1.7, 0.55, 1]}
      />
    </group>
  );
}
